// Shared constants for the Poll Management module.
// Mirrors modules/announcements/constants/announcementOptions.js so both
// CoreHR modules read the same way.

// Absolute route paths (mounted under AppRoutes at /*).
export const ROUTES = {
  essDashboard:  "/hrms-ess/polls",
  essVote:       (id) => `/hrms-ess/polls/vote/${id}`,
  essResults:    (id) => `/hrms-ess/polls/results/${id}`,
  creatorList:   "/hrms-team/polls",
  hrLog:         "/hrms-team/polls/log",
  creatorResults: (id) => `/hrms-team/polls/results/${id}`,
};

// Screen 2 — the six audience groups. Reportees/Department take a qualifier.
export const AUDIENCE_GROUPS = [
  { key: "Global",     label: "Global",     hint: "Everyone in the organization" },
  { key: "Manager",    label: "Manager",    hint: "All employees with a manager role" },
  { key: "Reportees",  label: "Reportees",  hint: "Direct reports of one manager, one level down" },
  { key: "Directors",  label: "Directors",  hint: "All directors" },
  { key: "Department", label: "Department", hint: "All active employees in one department" },
  { key: "HR",         label: "HR",         hint: "The HR team" },
];

export const RESPONSE_TYPES = [
  { key: "Single Choice",   label: "Single Choice",   hint: "One option per voter", needsOptions: true },
  { key: "Multiple Choice", label: "Multiple Choice", hint: "One or more options",  needsOptions: true },
  { key: "Rating",          label: "Rating",          hint: "Fixed 1–5 scale",      needsOptions: false },
];

// Poll status model. No HR review gate and no scheduling: every creator
// publishes directly and a published poll is Active immediately, so a poll
// goes Draft -> Active -> Closed. (Scheduled stays defined for the status
// pill only; it is never produced now that start is always "today".)
export const POLL_STATUSES = ["Draft", "Active", "Closed"];

// Reuses the shared --status-* tokens from styles/globals.css.
export const STATUS_STYLES = {
  Draft:       { bg: "var(--status-warning-bg)",   color: "var(--status-warning-fg)",   dot: "var(--status-warning-dot)" },
  Scheduled:   { bg: "var(--status-scheduled-bg)", color: "var(--status-scheduled-fg)", dot: "var(--status-scheduled-dot)" },
  Active:      { bg: "var(--status-published-bg)", color: "var(--status-published-fg)", dot: "var(--status-published-dot)" },
  Closed:      { bg: "var(--status-archived-bg)",  color: "var(--status-archived-fg)",  dot: "var(--status-archived-dot)" },
  // Employee-facing only: "you have voted on this one", shown on the
  // Completed tab whether or not the poll itself is still open.
  Completed:   { bg: "var(--status-published-bg)", color: "var(--status-published-fg)", dot: "var(--status-published-dot)" },
};

// Left accent bar per response type — keeps cards scannable at a glance.
export const RESPONSE_TYPE_COLOR = {
  "Single Choice":   "var(--color-accent)",
  "Multiple Choice": "var(--status-submitted-fg)",
  "Rating":          "var(--color-warning)",
};

export const MIN_OPTIONS = 2;
export const MAX_OPTIONS = 10;
export const RATING_SCALE = [1, 2, 3, 4, 5];

/** Today as YYYY-MM-DD for the read-only start date and the end-date min. */
export function todayISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** A stored ISO datetime as YYYY-MM-DD for <input type="date">. */
export function toDateInputValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** A stored ISO datetime as "HH:mm" (24h) for the time dropdown. */
export function toTimeInputValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Half-hourly time options for the end-time dropdown: { value:"HH:mm",
 * label:"h:mm AM/PM" }. A plain dropdown reads more simply than a native
 * time spinner and avoids the seconds/locale quirks of <input type=time>.
 */
export const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h24 = Math.floor(i / 2);
  const min = i % 2 === 0 ? "00" : "30";
  const value = `${String(h24).padStart(2, "0")}:${min}`;
  const period = h24 < 12 ? "AM" : "PM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return { value, label: `${h12}:${min} ${period}` };
});

/** Default end time when creating a poll: 6:00 PM. */
export const DEFAULT_END_TIME = "18:00";

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

export function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? ""
    : d.toLocaleString("en-GB", {
        day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
      });
}

/** "in 3 hours" / "2 days left" style copy for the employee dashboard. */
export function timeRemaining(endDateTime) {
  const ms = new Date(endDateTime).getTime() - Date.now();
  if (isNaN(ms) || ms <= 0) return "Closed";
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return `${Math.max(1, Math.floor(ms / 60_000))} min left`;
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} left`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} left`;
}

/** Amber urgency indicator: 5 hours or less to End Date & Time. */
export const URGENT_WINDOW_HOURS = 5;

export function isUrgent(endDateTime) {
  const ms = new Date(endDateTime).getTime() - Date.now();
  return ms > 0 && ms <= URGENT_WINDOW_HOURS * 3_600_000;
}

/** "Reportees — Mgr: Rakesh" style one-liner used on cards and detail rows. */
export function describeAudience(audience) {
  if (!audience?.group) return "—";
  if (audience.group === "Department" && audience.departmentName) {
    return `Department — ${audience.departmentName}`;
  }
  if (audience.group === "Reportees" && audience.managerName) {
    return `Reportees — Mgr: ${audience.managerName}`;
  }
  return audience.group;
}
